// What the asset library, screenshots and card rendering say over HTTP.

import { z } from "zod";
import { IdPageQuery, pageOf } from "./common";

export const ASSET_KINDS = ["photo", "logo", "screenshot", "card", "illustration", "document", "video"] as const;
export const AssetKind = z.enum(ASSET_KINDS);
export type AssetKind = z.infer<typeof AssetKind>;

export const ASSET_SOURCES = ["host_feed", "upload", "capture", "card", "ai", "product_photo"] as const;
export const AssetSource = z.enum(ASSET_SOURCES);
export type AssetSource = z.infer<typeof AssetSource>;

export const ASSET_STATUSES = ["active", "retired"] as const;
export const AssetStatus = z.enum(ASSET_STATUSES);
export type AssetStatus = z.infer<typeof AssetStatus>;

export const RIGHTS_BASES = ["own", "licensed", "host_provided", "generated", "unknown"] as const;
export const RightsBasis = z.enum(RIGHTS_BASES);
export type RightsBasis = z.infer<typeof RightsBasis>;

export const ASSET_USED_IN = ["card", "post", "ad", "email", "landing", "other"] as const;
export const AssetUsedIn = z.enum(ASSET_USED_IN);
export type AssetUsedIn = z.infer<typeof AssetUsedIn>;

const AssetTag = z.string().trim().toLowerCase().min(1).max(40);
const AssetTags = z.array(AssetTag).max(30);

export const AssetDto = z.object({
  id: z.string(),
  kind: AssetKind,
  source: AssetSource,
  status: AssetStatus,
  title: z.string().nullable(),
  alt: z.string().nullable(),
  tags: z.array(z.string()),
  /** Short-lived. Null on a host photo that has not been copied here yet; `externalUrl` has it then. */
  url: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  externalUrl: z.string().nullable(),
  mimeType: z.string().nullable(),
  byteSize: z.number().int().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  /** A video's length. */
  durationMs: z.number().int().nullable(),
  /** A document's pages. */
  pages: z.number().int().nullable(),
  /** The render that drew it: the slides of one deck share it, and it finds the others from any one of them. */
  renderId: z.string().nullable(),
  /** On a picture made from a product photo: why the original was kept, one of PHOTO_DEGRADED_REASONS. */
  degradedBy: z.string().nullable(),
  /** The picture standing for a video in a feed. */
  coverAssetId: z.string().nullable(),
  containsPeople: z.boolean().nullable(),
  containsThirdParty: z.boolean(),
  consentRecorded: z.boolean(),
  consentNote: z.string().nullable(),
  consentByName: z.string().nullable(),
  consentAt: z.string().nullable(),
  rightsBasis: RightsBasis,
  rightsHolder: z.string().nullable(),
  rightsExpiresAt: z.string().nullable(),
  retiredReason: z.string().nullable(),
  retiredAt: z.string().nullable(),
  /** Why the asset cannot go on a card right now; empty when it can. */
  problems: z.array(z.string()),
  usable: z.boolean(),
  /** How many posts, ads and cards it is in. */
  usageCount: z.number().int(),
  /** A model made it. The label a publisher or a viewer has to be able to see, wherever the picture goes. */
  generatedByAi: z.boolean(),
  /** For a generated picture: which provider and model, the check it passed, the prompt. */
  provenance: z.unknown().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
});
export type AssetDto = z.infer<typeof AssetDto>;
export const AssetPage = pageOf(AssetDto);

export const AssetListQuery = IdPageQuery.extend({
  kind: AssetKind.optional(),
  source: AssetSource.optional(),
  status: AssetStatus.optional(),
  /** Comma-separated; an asset has to carry all of them. */
  tags: z.string().max(400).optional(),
  /** Searches the title, the alt text and the tags. */
  q: z.string().trim().min(1).max(100).optional(),
  usable: z.enum(["true", "false"]).optional(),
});
export type AssetListQuery = z.infer<typeof AssetListQuery>;

const FormBoolean = z.enum(["true", "false"]).transform((value) => value === "true");

/** The form fields of an upload. `containsPeople` is required: somebody states it, or the photo stays unusable. */
export const AssetUploadFields = z.object({
  title: z.string().trim().max(200).optional(),
  alt: z.string().trim().max(500).optional(),
  /** Comma-separated. */
  tags: z.string().max(1_000).optional(),
  kind: z.enum(["photo", "logo", "illustration", "document"]).default("photo"),
  containsPeople: FormBoolean,
  containsThirdParty: FormBoolean.optional(),
  consentRecorded: FormBoolean.optional(),
  consentNote: z.string().trim().max(500).optional(),
  rightsBasis: RightsBasis.default("own"),
  rightsHolder: z.string().trim().max(200).optional(),
  rightsExpiresAt: z.iso.datetime().optional(),
});
export type AssetUploadFields = z.infer<typeof AssetUploadFields>;

/** The same statements for a video or a PDF. What it is comes from its bytes, never from the form. */
export const AssetFileFields = AssetUploadFields.omit({ kind: true });
export type AssetFileFields = z.infer<typeof AssetFileFields>;

export const AssetPatch = z
  .object({
    title: z.string().trim().max(200).nullable(),
    alt: z.string().trim().max(500).nullable(),
    tags: AssetTags,
    containsPeople: z.boolean(),
    containsThirdParty: z.boolean(),
    consentRecorded: z.boolean(),
    consentNote: z.string().trim().max(500).nullable(),
    rightsBasis: RightsBasis,
    rightsHolder: z.string().trim().max(200).nullable(),
    rightsExpiresAt: z.iso.datetime().nullable(),
  })
  .partial();
export type AssetPatch = z.infer<typeof AssetPatch>;

export const AssetUsageCreate = z.object({
  usedIn: AssetUsedIn,
  ref: z.string().trim().min(1).max(200),
  label: z.string().trim().max(200).optional(),
});
export type AssetUsageCreate = z.infer<typeof AssetUsageCreate>;

export const AssetUsageDto = z.object({
  id: z.string(),
  usedIn: AssetUsedIn,
  ref: z.string(),
  label: z.string().nullable(),
  createdAt: z.string(),
});
export type AssetUsageDto = z.infer<typeof AssetUsageDto>;
export const AssetUsagePage = pageOf(AssetUsageDto);

// ---------------------------------------------------------------------------
// Screenshots
// ---------------------------------------------------------------------------

export const CAPTURE_VIEWPORTS = ["desktop", "phone"] as const;
export const CaptureViewport = z.enum(CAPTURE_VIEWPORTS);
export type CaptureViewport = z.infer<typeof CaptureViewport>;

export const CAPTURE_STATUSES = ["queued", "running", "done", "failed"] as const;

export const CaptureCreate = z.object({
  url: z.string().trim().min(1).max(2_000),
  viewport: CaptureViewport.default("desktop"),
  fullPage: z.boolean().default(true),
  deviceFrame: z.boolean().default(false),
});
export type CaptureCreate = z.infer<typeof CaptureCreate>;

export const CaptureDto = z.object({
  id: z.string(),
  url: z.string(),
  finalUrl: z.string().nullable(),
  viewport: CaptureViewport,
  fullPage: z.boolean(),
  deviceFrame: z.boolean(),
  status: z.enum(CAPTURE_STATUSES),
  errorCode: z.string().nullable(),
  assetId: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
});
export type CaptureDto = z.infer<typeof CaptureDto>;
export const CapturePage = pageOf(CaptureDto);

export const CaptureDomainCreate = z.object({ domain: z.string().trim().toLowerCase().min(1).max(253) });
export type CaptureDomainCreate = z.infer<typeof CaptureDomainCreate>;

export const CaptureDomainDto = z.object({ id: z.string(), domain: z.string(), addedByName: z.string().nullable(), createdAt: z.string() });
export type CaptureDomainDto = z.infer<typeof CaptureDomainDto>;
export const CaptureDomainList = z.object({ items: z.array(CaptureDomainDto) });

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

/**
 * Meta's guide for what the Reels interface covers on a 9:16 frame, as a share of each side. The one source of these
 * numbers: the card renderer lays out against them and the channel previews warn with them, so the two cannot disagree.
 */
export const REEL_SAFE_ZONE = { top: 0.14, bottom: 0.35, left: 0.06, right: 0.06 } as const;
/** Meta's guide for what Stories cover. */
export const STORY_SAFE_ZONE = { top: 0.14, bottom: 0.2, left: 0, right: 0 } as const;
/** The caption and the right-hand buttons of a short vertical video (TikTok, Shorts). */
export const SHORT_VIDEO_SAFE_ZONE = { top: 0.085, bottom: 0.22, left: 0.02, right: 0.14 } as const;

export const CardFormatName = z.enum(["1:1", "4:5", "9:16", "1.91:1"]);
export type CardFormatName = z.infer<typeof CardFormatName>;
/** Which vertical surface a 9:16 card is made for. Left out, it is `reels`, whose zone is clear of Stories too. */
export const CARD_PLACEMENTS = ["reels", "stories", "shorts", "tiktok"] as const;
export const CardPlacementName = z.enum(CARD_PLACEMENTS);
export type CardPlacementName = z.infer<typeof CardPlacementName>;
export const CardThemeName = z.enum(["light", "primary", "accent", "soft"]);
export type CardThemeName = z.infer<typeof CardThemeName>;

export const ImageFindingDto = z.object({
  code: z.string(),
  severity: z.enum(["block", "warn", "info"]),
  message: z.string(),
  box: z.object({ x: z.number(), y: z.number(), width: z.number(), height: z.number() }).optional(),
  text: z.string().optional(),
  data: z.record(z.string(), z.unknown()).optional(),
});
export type ImageFindingDto = z.infer<typeof ImageFindingDto>;

export const ImageCheckDto = z.object({
  findings: z.array(ImageFindingDto),
  blocked: z.boolean(),
  metrics: z.object({
    fitScale: z.number(),
    reflowed: z.boolean(),
    textAreaShare: z.number(),
    lowestContrast: z.number().nullable(),
  }),
});
export type ImageCheckDto = z.infer<typeof ImageCheckDto>;

/** An image slot holds `asset:<id>` of a library picture of this tenant; nothing else is taken, so a tenant cannot make the service fetch a URL. */
export const CardSpec = z.object({
  template: z.string().min(1).max(40),
  slots: z.record(z.string(), z.unknown()),
  format: CardFormatName.default("1:1"),
  /** Only a 9:16 card has one. */
  placement: CardPlacementName.optional(),
  theme: CardThemeName.default("light"),
  /** Language of the words the renderer draws itself. */
  locale: z.string().trim().min(2).max(10).default("ro"),
});
export type CardSpec = z.infer<typeof CardSpec>;

export const CardCheckRequest = CardSpec.extend({
  purpose: z.enum(["organic", "ad"]).default("organic"),
  alt: z.string().max(500).nullable().optional(),
  postLocale: z.string().trim().min(2).max(10).optional(),
  /** Fact ids the numbers on the card must come from. Left out, numbers are checked against every usable fact. */
  factIds: z.array(z.uuid()).max(200).optional(),
});
export type CardCheckRequest = z.infer<typeof CardCheckRequest>;

export const VISUAL_RENDER_STATUSES = ["queued", "running", "done", "failed"] as const;

export const VISUAL_RENDER_KINDS = ["card", "product_photo", "illustration", "carousel", "video", "upload"] as const;

/** Why a product photo came out with the original picture, when something else was asked for. */
export const PHOTO_DEGRADED_REASONS = ["photo_edit_rejected", "photo_edit_failed", "photo_edit_not_configured", "photo_edit_people", "photo_edit_regulated", "photo_edit_limit", "photo_edit_key_required", "photo_edit_people_own_keys", "photo_edit_regulated_own_keys"] as const;
export type PhotoDegradedReason = (typeof PHOTO_DEGRADED_REASONS)[number];

export const VisualRenderDto = z.object({
  id: z.string(),
  kind: z.enum(VISUAL_RENDER_KINDS),
  status: z.enum(VISUAL_RENDER_STATUSES),
  contentHash: z.string(),
  errorCode: z.string().nullable(),
  /** Fit and findings per image, or why it failed. */
  report: z.unknown().nullable(),
  /** A product photo only: the original picture was kept, and this is why (`PHOTO_DEGRADED_REASONS`). Null when the change was made, or none was asked for. */
  degradedBy: z.string().nullable(),
  assetIds: z.array(z.string()),
  /** True when an earlier, identical request already drew it. */
  cached: z.boolean(),
  createdAt: z.string(),
});
export type VisualRenderDto = z.infer<typeof VisualRenderDto>;

/** One slot of a card template, as an editor needs it. */
export const CardSlotInfo: z.ZodType<CardSlotInfo> = z.lazy(() =>
  z.object({
    name: z.string(),
    kind: z.enum(["text", "url", "image", "list", "group"]),
    required: z.boolean(),
    max: z.number().int().optional(),
    min: z.number().int().optional(),
    pattern: z.string().optional(),
    /** A list: what each of its entries is, under the name "item". */
    of: CardSlotInfo.optional(),
    /** A group: its fields. */
    fields: z.array(CardSlotInfo).optional(),
  }),
);
export interface CardSlotInfo {
  name: string;
  kind: "text" | "url" | "image" | "list" | "group";
  required: boolean;
  max?: number | undefined;
  min?: number | undefined;
  pattern?: string | undefined;
  of?: CardSlotInfo | undefined;
  fields?: CardSlotInfo[] | undefined;
}

export const CardTemplateInfo = z.object({ id: z.string(), reflows: z.boolean(), schema: z.unknown(), slots: z.array(CardSlotInfo) });
export type CardTemplateInfo = z.infer<typeof CardTemplateInfo>;
export const CardTemplateList = z.object({
  templates: z.array(CardTemplateInfo),
  formats: z.array(CardFormatName),
  placements: z.array(CardPlacementName),
  themes: z.array(CardThemeName),
});

// ---------------------------------------------------------------------------
// Product photo to visuals
// ---------------------------------------------------------------------------

const ProductPhotoCommon = {
  /** A library photo of this tenant. */
  assetId: z.uuid(),
  title: z.string().trim().min(1).max(90),
  subtitle: z.string().trim().max(180).optional(),
  badge: z.string().trim().max(32).optional(),
  ctaUrl: z.url().max(600).optional(),
  background: z.discriminatedUnion("mode", [
    /** Cut out, on the card's own ground. */
    z.object({ mode: z.literal("remove") }),
    /** Cut out, on one of the brand's colours. */
    z.object({ mode: z.literal("brand") }),
    /** Cut out, on another library picture. */
    z.object({ mode: z.literal("image"), assetId: z.uuid() }),
    /** As it is. */
    z.object({ mode: z.literal("keep") }),
  ]),
  theme: CardThemeName.default("light"),
  locale: z.string().trim().min(2).max(10).default("ro"),
};

/**
 * `ai`: a model filled this in, so the price can only come from an approved fact; there is no field for the words of a
 * price, and `strict` refuses one. `manual`: a person typed it, and the image checks still hold the number against the facts.
 */
export const ProductPhotoRequest = z.discriminatedUnion("mode", [
  z.strictObject({ mode: z.literal("ai"), priceFactId: z.uuid().optional(), ...ProductPhotoCommon }),
  z.strictObject({ mode: z.literal("manual"), priceText: z.string().trim().max(24).optional(), ...ProductPhotoCommon }),
]);
export type ProductPhotoRequest = z.infer<typeof ProductPhotoRequest>;

// ---------------------------------------------------------------------------
// AI illustrations
// ---------------------------------------------------------------------------

export const ILLUSTRATION_STYLES = ["flat", "clay", "paper", "abstract", "background"] as const;
export const IllustrationStyle = z.enum(ILLUSTRATION_STYLES);
export type IllustrationStyle = z.infer<typeof IllustrationStyle>;

export const IllustrationAspect = z.enum(["1:1", "4:5", "9:16", "16:9"]);
export type IllustrationAspect = z.infer<typeof IllustrationAspect>;

export const IllustrationProviderName = z.enum(["gemini", "openai"]);
export type IllustrationProviderName = z.infer<typeof IllustrationProviderName>;

export const IllustrationRequest = z
  .strictObject({
    style: IllustrationStyle.default("flat"),
    /** One of the subjects the options list for this tenant. The only way in on a regulated vertical. */
    subject: z.string().trim().min(1).max(60).optional(),
    /** A free description of what to draw, on a vertical that is not regulated. */
    topic: z.string().trim().min(3).max(600).optional(),
    /** The words of a poster. Spelling is checked against these, letter by letter, in the image that comes back. */
    posterText: z.string().trim().min(1).max(200).optional(),
    aspect: IllustrationAspect.default("1:1"),
    /** Tries this provider first. Left out: OpenAI for a poster, the tenant's choice for the rest. */
    provider: IllustrationProviderName.optional(),
    /** Language of the poster's words, and of the alt text. */
    locale: z.string().trim().min(2).max(10).default("ro"),
    /** Only an owner may ask, and never on a regulated vertical. */
    allowPeople: z.boolean().default(false),
    /** The same request gives the same picture back; another number asks for another one. */
    variant: z.number().int().min(0).max(1_000).default(0),
    title: z.string().trim().max(200).optional(),
    alt: z.string().trim().max(500).optional(),
    tags: z.array(z.string().trim().toLowerCase().min(1).max(40)).max(20).optional(),
  })
  .refine((value) => (value.subject === undefined) !== (value.topic === undefined), { message: "exactly one of subject and topic", path: ["subject"] });
export type IllustrationRequest = z.infer<typeof IllustrationRequest>;

export const IllustrationOptions = z.object({
  styles: z.array(IllustrationStyle),
  subjects: z.array(z.string()),
  /** Whether `topic` is taken. */
  freePrompt: z.boolean(),
  /** Whether `posterText` is taken. */
  posters: z.boolean(),
  /** Whether this caller may send `allowPeople`. */
  people: z.boolean(),
  providers: z.array(IllustrationProviderName),
  defaultProvider: IllustrationProviderName,
});
export type IllustrationOptions = z.infer<typeof IllustrationOptions>;

// ---------------------------------------------------------------------------
// Carousels and documents
// ---------------------------------------------------------------------------

export const CAROUSEL_SLIDE_LIMITS = { min: 3, max: 10 } as const;

const SlideSlots = z.record(z.string(), z.unknown());

/** A cover, what the deck says and a closing slide, one theme and one format throughout. Image slots hold `asset:<id>`, as on a card. */
export const CarouselRequest = z.strictObject({
  cover: SlideSlots,
  slides: z
    .array(z.strictObject({ template: z.string().min(1).max(40), slots: SlideSlots }))
    .min(CAROUSEL_SLIDE_LIMITS.min - 2)
    .max(CAROUSEL_SLIDE_LIMITS.max - 2),
  cta: SlideSlots,
  format: CardFormatName.default("1:1"),
  /** Which surface's safe zone a 9:16 deck keeps clear of. Without one it stays clear of Reels and Stories. */
  placement: CardPlacementName.optional(),
  theme: CardThemeName.default("light"),
  locale: z.string().trim().min(2).max(10).default("ro"),
  /** One image per slide, and a PDF of them all for a LinkedIn document post. */
  exports: z.array(z.enum(["images", "pdf"])).min(1).max(2).default(["images", "pdf"]),
  title: z.string().trim().max(200).optional(),
});
export type CarouselRequest = z.infer<typeof CarouselRequest>;

// ---------------------------------------------------------------------------
// Short video
// ---------------------------------------------------------------------------

export const VIDEO_SCENE_LIMITS = { min: 3, max: 6 } as const;
/** What a person may set for one scene. Unset, the scene holds as long as its text takes to read. */
export const VIDEO_SCENE_DURATION_MS = { min: 1500, max: 8000, step: 500 } as const;
/** How long a scene holds when nobody set it: time to read its words, within these bounds. */
export const VIDEO_READING_PACE = { msPerChar: 60, minMs: 2500, maxMs: 5000 } as const;
/** The longest video produced here, on every channel that takes one. */
export const VIDEO_MAX_MS = 45_000;
/** A cover picture opens the video this long, so it is also what a feed shows on a channel that takes the first frame. */
export const VIDEO_COVER_FRAME_MS = 1500;

const SceneDuration = z.number().int().min(VIDEO_SCENE_DURATION_MS.min).max(VIDEO_SCENE_DURATION_MS.max);

/** A library picture with its subtitle, or a slide drawn from a template. */
export const VideoScene = z.union([
  z.strictObject({ assetId: z.uuid(), subtitle: z.string().trim().min(1).max(90).optional(), durationMs: SceneDuration.optional() }),
  z.strictObject({ template: z.string().min(1).max(40), slots: SlideSlots, durationMs: SceneDuration.optional() }),
]);
export type VideoScene = z.infer<typeof VideoScene>;

/** A 9:16 slideshow. Each scene burns its subtitle into the picture; the video is silent, with no audio track at all. */
export const VideoRequest = z
  .strictObject({
    scenes: z.array(VideoScene).min(VIDEO_SCENE_LIMITS.min).max(VIDEO_SCENE_LIMITS.max),
    /** The scene that opens the video and stands for it in a feed. The others keep their order. */
    coverIndex: z.number().int().min(0).default(0),
    /** A picture of the library as the cover instead of a scene. It opens the video for a moment, and `coverIndex` then means nothing. */
    coverAssetId: z.uuid().optional(),
    /** Off: the pictures alone, whatever lines the scenes carry. */
    subtitles: z.boolean().default(true),
    /** Whose interface the frames keep clear of. Without one they stay clear of Reels and Stories. */
    placement: CardPlacementName.optional(),
    theme: CardThemeName.default("light"),
    locale: z.string().trim().min(2).max(10).default("ro"),
    title: z.string().trim().max(200).optional(),
  })
  .refine((value) => value.coverIndex < value.scenes.length, { message: "coverIndex is past the last scene", path: ["coverIndex"] });
export type VideoRequest = z.infer<typeof VideoRequest>;

// ---------------------------------------------------------------------------
// Drafting slides from a post's text
// ---------------------------------------------------------------------------

/** What a person gives to get a deck to edit: the post's text and how many slides between the cover and the close. */
export const DeckDraftRequest = z.strictObject({
  text: z.string().trim().min(1).max(5000),
  slides: z.number().int().min(CAROUSEL_SLIDE_LIMITS.min - 2).max(CAROUSEL_SLIDE_LIMITS.max - 2).default(4),
  locale: z.string().trim().min(2).max(10).default("ro"),
});
export type DeckDraftRequest = z.infer<typeof DeckDraftRequest>;

/** A deck to edit, nothing drawn or stored yet. */
export const DeckDraftDto = z.object({
  cover: z.object({ title: z.string(), subtitle: z.string().optional() }),
  slides: z.array(z.object({ template: z.string(), slots: z.record(z.string(), z.unknown()) })),
  cta: z.object({ title: z.string() }),
  /** For a video: one short line per slide. */
  lines: z.array(z.string()),
});
export type DeckDraftDto = z.infer<typeof DeckDraftDto>;

// ---------------------------------------------------------------------------
// Limits on files, read by the worker that makes them and by the checks that place them
// ---------------------------------------------------------------------------

export const FILE_BYTE_LIMITS = {
  /** LinkedIn's limit on a document post. */
  document: 100 * 1024 * 1024,
  /** Telegram's limit on a video sent by URL, the smallest of the channels that take one. What is made here stays under it. */
  producedVideo: 20 * 1024 * 1024,
  /** What a person may bring. A channel may take less: its own check says so on the post. */
  uploadedVideo: 100 * 1024 * 1024,
} as const;

/** LinkedIn's limit on a document's pages. */
export const DOCUMENT_PAGES_MAX = 300;
